/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP List Page
 * =============================================================================
 *
 * Displays a list of RFPs with filtering, sorting, and pagination.
 * Users can navigate to RFP details or create new RFPs from this page.
 *
 * Features:
 * - Filter by status
 * - Search by title/RFP number
 * - Sort by various fields
 * - Pagination
 * - Quick status badges
 *
 * @module pages/rfps/RFPList
 * @author SPEQ55
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { get, upload, post, del } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'tracking', label: 'Tracking' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'no_bid', label: 'No Bid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest First' },
  { value: 'created_at', label: 'Oldest First' },
  { value: 'submission_deadline', label: 'Deadline (Soonest)' },
  { value: '-submission_deadline', label: 'Deadline (Latest)' },
  { value: 'title', label: 'Title (A-Z)' },
  { value: '-title', label: 'Title (Z-A)' },
];

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

/**
 * Status badge component for RFP status display.
 */
function StatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', className: 'badge bg-accent-100 text-accent-700' },
    tracking: {
      label: 'Tracking',
      className: 'badge bg-info-light text-info-dark',
    },
    no_bid: {
      label: 'No Bid',
      className: 'badge bg-accent-200 text-accent-600',
    },
    in_progress: {
      label: 'In Progress',
      className: 'badge bg-warning-light text-warning-dark',
    },
    submitted: {
      label: 'Submitted',
      className: 'badge bg-secondary-100 text-secondary-700',
    },
    won: {
      label: 'Won',
      className: 'badge bg-success-light text-success-dark',
    },
    lost: { label: 'Lost', className: 'badge bg-error-light text-error-dark' },
    cancelled: {
      label: 'Cancelled',
      className: 'badge bg-accent-200 text-accent-500',
    },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
}

/**
 * Format date for display.
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate days until deadline.
 */
function getDaysUntil(dateString) {
  if (!dateString) return null;
  const deadline = new Date(dateString);
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// =============================================================================
// RFP LIST COMPONENT
// =============================================================================

function RFPList() {
  const navigate = useNavigate();
  const { hasMinRole } = useAuth();

  // State
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('-created_at');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Action state
  const [actionRfp, setActionRfp] = useState(null); // RFP being acted upon
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Can user create RFPs?
  const canCreate = hasMinRole('org_admin');

  /**
   * Fetch RFPs from API.
   */
  useEffect(() => {
    const fetchRFPs = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          sort,
          limit,
          offset: (page - 1) * limit,
        };

        if (status) params.status = status;
        if (search) params.search = search;

        const response = await get('/rfps', params);
        setRfps(response.data.rfps || []);
        setTotal(response.data.total || 0);
      } catch {
        setError('Failed to load RFPs. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRFPs();
  }, [status, search, sort, page]);

  /**
   * Handle search input with debounce.
   */
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  /**
   * Handle status filter change.
   */
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  /**
   * Handle sort change.
   */
  const handleSortChange = (e) => {
    setSort(e.target.value);
    setPage(1);
  };

  /**
   * Navigate to RFP detail page.
   */
  const handleRowClick = (rfpId) => {
    navigate(`/app/rfps/${rfpId}`);
  };

  /**
   * Handle file upload.
   */
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or Word document (.pdf, .doc, .docx)');
      return;
    }

    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('File is too large. Maximum size is 20MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const response = await upload('/rfps/upload', file, 'file', (progress) => {
        setUploadProgress(progress);
      });

      if (response.data.success) {
        // Navigate to the newly created RFP
        navigate(`/app/rfps/${response.data.rfp.id}`);
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Handle file input change.
   */
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  /**
   * Handle drag events.
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  /**
   * Handle file drop.
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  /**
   * Open file picker.
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle file download/view - fetches with auth and opens in new tab.
   */
  const handleViewFile = async (e, rfpId, _filename) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const response = await api.get(`/rfps/${rfpId}/download`, {
        responseType: 'blob',
      });

      // Create blob URL and open in new tab
      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      alert('Failed to open file. Please try again.');
    }
  };

  /**
   * Open delete confirmation modal.
   */
  const openDeleteModal = (e, rfp) => {
    e.stopPropagation();
    setActionRfp(rfp);
    setActionError(null);
    setShowDeleteModal(true);
  };

  /**
   * Open copy confirmation modal.
   */
  const openCopyModal = (e, rfp) => {
    e.stopPropagation();
    setActionRfp(rfp);
    setActionError(null);
    setShowCopyModal(true);
  };

  /**
   * Handle RFP deletion.
   */
  const handleDelete = async () => {
    if (!actionRfp) return;

    setActionLoading(true);
    setActionError(null);

    try {
      await del(`/rfps/${actionRfp.id}`);
      // Remove from list
      setRfps((prev) => prev.filter((r) => r.id !== actionRfp.id));
      setTotal((prev) => prev - 1);
      setShowDeleteModal(false);
      setActionRfp(null);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to delete RFP. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle RFP copy/duplicate.
   */
  const handleCopy = async () => {
    if (!actionRfp) return;

    setActionLoading(true);
    setActionError(null);

    try {
      const response = await post(`/rfps/${actionRfp.id}/copy`);
      // Add copied RFP to the list
      if (response.data.rfp) {
        setRfps((prev) => [response.data.rfp, ...prev]);
        setTotal((prev) => prev + 1);
      }
      setShowCopyModal(false);
      setActionRfp(null);
      // Navigate to the new RFP
      navigate(`/app/rfps/${response.data.rfp.id}`);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to copy RFP. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Close action modals.
   */
  const closeModals = () => {
    setShowDeleteModal(false);
    setShowCopyModal(false);
    setActionRfp(null);
    setActionError(null);
  };

  // Calculate pagination
  const totalPages = Math.ceil(total / limit);

  // Loading state
  if (loading && rfps.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading RFPs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-800">RFPs</h1>
          <p className="mt-1 text-accent-500">Manage your requests for proposals</p>
        </div>
        {canCreate && (
          <Link to="/app/rfps/create" className="btn-primary">
            <span className="mr-2">+</span>
            New RFP
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title or RFP number..."
              value={search}
              onChange={handleSearchChange}
              className="input w-full"
            />
          </div>

          {/* Status Filter */}
          <div className="w-full sm:w-48">
            <select value={status} onChange={handleStatusChange} className="input w-full">
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="w-full sm:w-48">
            <select value={sort} onChange={handleSortChange} className="input w-full">
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="card p-4">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileInputChange}
          className="hidden"
        />

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFilePicker}
          className={`
            p-6 border-2 border-dashed rounded-lg cursor-pointer
            transition-colors duration-200 text-center
            ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-accent-300 hover:border-primary-400 hover:bg-accent-50'
            }
            ${uploading ? 'pointer-events-none opacity-75' : ''}
          `}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-4">
              <div className="w-6 h-6 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
              <span className="text-sm text-accent-600">Uploading... {uploadProgress}%</span>
              <div className="w-32 bg-accent-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">📤</span>
              <div className="text-left">
                <p className="text-sm font-medium text-accent-700">
                  Drop RFP document here or click to upload
                </p>
                <p className="text-xs text-accent-400">PDF or Word documents (max 20MB)</p>
              </div>
            </div>
          )}
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="mt-3 p-3 bg-error-light border border-error rounded-lg">
            <p className="text-sm text-error-dark">{uploadError}</p>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* RFP List */}
      <div className="card">
        {rfps.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-4xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-accent-800">No RFPs found</h3>
            <p className="mt-1 text-accent-500">
              {search || status
                ? 'Try adjusting your filters'
                : 'Use the upload area above to add your first RFP'}
            </p>
            {canCreate && !search && !status && (
              <Link to="/app/rfps/create" className="btn-outline mt-4 inline-block">
                Or create manually
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="px-6 py-3 border-b border-accent-200 bg-accent-50">
              <p className="text-sm text-accent-600">
                Showing {rfps.length} of {total} RFPs
              </p>
            </div>

            {/* RFP table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-accent-200 bg-accent-50 text-left text-xs font-semibold uppercase tracking-wide text-accent-500">
                    <th className="px-6 py-3">Title / RFP #</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Created By</th>
                    <th className="px-4 py-3">Deadline</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-accent-100">
                  {rfps.map((rfp) => {
                    const daysUntil = getDaysUntil(rfp.submissionDeadline);
                    const isUrgent = daysUntil !== null && daysUntil <= 7 && daysUntil >= 0;
                    const isPast = daysUntil !== null && daysUntil < 0;

                    return (
                      <tr
                        key={rfp.id}
                        onClick={() => handleRowClick(rfp.id)}
                        className="hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        {/* Title + RFP number */}
                        <td className="px-6 py-3">
                          <p className="font-medium text-accent-800 truncate max-w-xs">
                            {rfp.title}
                          </p>
                          <p className="text-xs font-mono text-accent-400 mt-0.5">
                            {rfp.rfpNumber}
                          </p>
                        </td>

                        {/* Company */}
                        <td className="px-4 py-3 text-accent-600 whitespace-nowrap">
                          {rfp.companyName || <span className="text-accent-300">—</span>}
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3 text-accent-600 capitalize whitespace-nowrap">
                          {rfp.category ? (
                            rfp.category.replace('_', ' ')
                          ) : (
                            <span className="text-accent-300">—</span>
                          )}
                        </td>

                        {/* Created By */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {rfp.createdByName ? (
                            <span className="text-accent-600">@{rfp.createdByName}</span>
                          ) : (
                            <span className="text-accent-300">—</span>
                          )}
                        </td>

                        {/* Deadline */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`text-xs ${
                              isPast
                                ? 'text-error font-medium'
                                : isUrgent
                                  ? 'text-warning-dark font-medium'
                                  : 'text-accent-500'
                            }`}
                          >
                            {isPast
                              ? `⚠ ${formatDate(rfp.submissionDeadline)}`
                              : formatDate(rfp.submissionDeadline)}
                            {isUrgent && !isPast && (
                              <span className="ml-1 text-warning-dark">({daysUntil}d)</span>
                            )}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={rfp.status} />
                        </td>

                        {/* Actions */}
                        <td
                          className="px-4 py-3 whitespace-nowrap text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {rfp.filePath && (
                              <button
                                onClick={(e) => handleViewFile(e, rfp.id, rfp.originalFilename)}
                                className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
                                title={`View ${rfp.originalFilename || 'document'}`}
                              >
                                PDF
                              </button>
                            )}
                            <button
                              onClick={(e) => openCopyModal(e, rfp)}
                              className="text-xs px-2 py-1 bg-accent-100 text-accent-700 rounded hover:bg-accent-200 transition-colors"
                              title="Duplicate this RFP"
                            >
                              Copy
                            </button>
                            <button
                              onClick={(e) => openDeleteModal(e, rfp)}
                              className="text-xs px-2 py-1 bg-error-light text-error-dark rounded hover:bg-error/20 transition-colors"
                              title="Delete this RFP"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-accent-200 flex items-center justify-between">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-accent-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && actionRfp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-error-light flex items-center justify-center">
                <span className="text-error text-xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-accent-800">Delete RFP?</h3>
            </div>

            <div className="mb-4">
              <p className="text-accent-600 mb-3">
                Are you sure you want to delete this RFP? This action cannot be undone.
              </p>
              <div className="bg-accent-50 rounded-lg p-3 border border-accent-200">
                <p className="font-medium text-accent-800">{actionRfp.title}</p>
                <p className="text-sm text-accent-500 font-mono">{actionRfp.rfpNumber}</p>
              </div>
              <div className="mt-3 p-3 bg-warning-light rounded-lg border border-warning">
                <p className="text-sm text-warning-dark">
                  <strong>Warning:</strong> Deleting this RFP will also remove any associated
                  proposals and uploaded files.
                </p>
              </div>
            </div>

            {actionError && (
              <div className="mb-4 p-3 bg-error-light border border-error rounded-lg">
                <p className="text-sm text-error-dark">{actionError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={closeModals} disabled={actionLoading} className="btn-outline">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-error text-white rounded-lg hover:bg-error-dark transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Deleting...' : 'Delete RFP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Confirmation Modal */}
      {showCopyModal && actionRfp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 text-xl">📋</span>
              </div>
              <h3 className="text-lg font-semibold text-accent-800">Copy RFP?</h3>
            </div>

            <div className="mb-4">
              <p className="text-accent-600 mb-3">
                This will create a duplicate of this RFP with a new RFP number. You can then edit
                the copy as needed.
              </p>
              <div className="bg-accent-50 rounded-lg p-3 border border-accent-200">
                <p className="font-medium text-accent-800">{actionRfp.title}</p>
                <p className="text-sm text-accent-500 font-mono">{actionRfp.rfpNumber}</p>
              </div>
              <div className="mt-3 p-3 bg-info-light rounded-lg border border-info">
                <p className="text-sm text-info-dark">
                  <strong>Note:</strong> The copy will be created as a new draft. Uploaded files
                  will not be copied.
                </p>
              </div>
            </div>

            {actionError && (
              <div className="mb-4 p-3 bg-error-light border border-error rounded-lg">
                <p className="text-sm text-error-dark">{actionError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={closeModals} disabled={actionLoading} className="btn-outline">
                Cancel
              </button>
              <button onClick={handleCopy} disabled={actionLoading} className="btn-primary">
                {actionLoading ? 'Copying...' : 'Create Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RFPList;
